/** Backend notification log → UI shape (channels, status, timeline). */
import type {
  BackendNotificationLog,
  NotificationLogUI,
  ChannelResultUI,
  NotificationStatus,
} from '../schema/notification';

function getDotStatus(success: boolean, error?: string): string {
  if (success) return 'success';
  if (error?.toLowerCase().includes('quota')) return 'quota';
  return 'failed';
}

export function transformLog(raw: BackendNotificationLog): NotificationLogUI {
  const channels: ChannelResultUI[] = raw.channelResults.map((cr) => ({
    type: cr.channel,
    status: cr.success ? 'delivered' : cr.retryable === false ? 'validation_failed' : 'failed',
    dotStatus: getDotStatus(cr.success, cr.error),
    provider: cr.providerUsed,
    msgId: cr.providerMessageId,
    error: cr.error,
  }));

  // Requested channels with no result yet render as pending.
  raw.requestedChannels.forEach((ch) => {
    if (!channels.find((c) => c.type === ch)) {
      channels.push({ type: ch, status: 'pending', dotStatus: 'pending' });
    }
  });

  return {
    id: raw.id,
    jobId: raw.idempotencyKey,
    source: raw.sourceModule,
    referenceId: raw.referenceId,
    template: raw.templateId,
    recipientLocale: raw.recipientLocale,
    requestedChannels: raw.requestedChannels,
    deliveredChannels: raw.deliveredChannels,
    shedFallbacks: raw.shedFallbacks,
    channels,
    status: raw.status as NotificationStatus,
    isFallback: raw.isFallback,
    circuitBreakerTripped: raw.circuitBreakerTripped,
    circuitBreakerInfo: raw.circuitBreakerInfo ?? null,
    fallbackSkipped: raw.fallbackSkipped,
    fallbackSkipReason: raw.fallbackSkipReason,
    retryCount: raw.retryCount ?? 0,
    retryMax: raw.retryMax ?? 5,
    productId: raw.productId,
    recipient: raw.recipientData ?? null,
    timeline: {
      queued: raw.queuedAt ? new Date(raw.queuedAt) : null,
      processing: raw.processedAt ? new Date(raw.processedAt) : null,
      terminal: raw.deliveredAt
        ? new Date(raw.deliveredAt)
        : raw.processedAt && raw.status !== 'processing' && raw.status !== 'queued'
          ? new Date(raw.processedAt)
          : null,
    },
  };
}

export const transformLogs = (raws: BackendNotificationLog[]): NotificationLogUI[] =>
  raws.map(transformLog);
